import React, { useEffect, useState, useCallback } from 'react';
import { employeeDirectoryService } from '../../services/employee-directory';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import {
  Search,
  Filter,
  ChevronDown,
  X,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  
  Briefcase,
  Grid3X3,
  List,
  ArrowUpDown,
  Users,
  Video,
  Shield,
  UserCheck
} from 'lucide-react';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  preferredName?: string;
  department: string;
  photoUrl?: string;
  birthday?: string;
  startDate?: string;
  namePronunciation?: string;
  workVideo?: string;
  manager?: string;
  phoneNumber?: string;
  personalEmail?: string;
  companyEmail?: string;
  timezone?: string;
  country?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  emergencyContact1Name?: string;
  emergencyContact1Method?: string;
  emergencyContact1Relationship?: string;
  emergencyContact2Name?: string;
  emergencyContact2Method?: string;
  emergencyContact2Relationship?: string;
  contactAuthorities?: string;
  lastUpdate?: string;
}

const Directory: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => {
    // Load saved view preference from localStorage with error handling
    try {
      const savedViewMode = localStorage.getItem('employee-directory-view-mode');
      return (savedViewMode === 'card' || savedViewMode === 'list') ? savedViewMode : 'card';
    } catch (error) {
      console.error('Failed to load view mode from localStorage:', error);
      return 'card';
    }
  });
  const [showSortOptions, setShowSortOptions] = useState(false);
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'date-latest' | 'date-earliest' | null>(null);

  // Get unique values for filters
  const departments = Array.from(new Set(employees.map(emp => emp.department).filter(Boolean))).sort();
  const countries = Array.from(new Set(employees.map(emp => emp.country).filter(Boolean))).sort();
  const timezones = Array.from(new Set(employees.map(emp => emp.timezone).filter(Boolean))).sort();

  const loadEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const employeesData = await employeeDirectoryService.getAllEmployees();
      setEmployees(employeesData);
    } catch (error) {
      console.error('Error loading employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const filterEmployees = useCallback(() => {
    let filtered = employees;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp =>
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(query) ||
        emp.companyEmail?.toLowerCase().includes(query) ||
        emp.personalEmail?.toLowerCase().includes(query)
      );
    }

    // Department filter
    if (selectedDepartment) {
      filtered = filtered.filter(emp => emp.department === selectedDepartment);
    }

    // Country filter
    if (selectedCountry) {
      filtered = filtered.filter(emp => emp.country === selectedCountry);
    }

    // Timezone filter
    if (selectedTimezone) {
      filtered = filtered.filter(emp => emp.timezone === selectedTimezone);
    }

    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return a.firstName.localeCompare(b.firstName);
          case 'name-desc':
            return b.firstName.localeCompare(a.firstName);
          case 'date-latest':
            // Put employees without start date at the bottom
            if (!a.startDate && !b.startDate) return 0;
            if (!a.startDate) return 1;
            if (!b.startDate) return -1;
            // Sort by date: latest first (newest to oldest)
            return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
          case 'date-earliest':
            // Put employees without start date at the bottom
            if (!a.startDate && !b.startDate) return 0;
            if (!a.startDate) return 1;
            if (!b.startDate) return -1;
            // Sort by date: earliest first (oldest to newest)
            return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
          default:
            return 0;
        }
      });
    }

    setFilteredEmployees(filtered);
  }, [employees, searchQuery, selectedDepartment, selectedCountry, selectedTimezone, sortBy]);

  useEffect(() => {
    loadEmployees();
  }, []);

  // Save view mode preference to localStorage with error handling
  useEffect(() => {
    try {
      localStorage.setItem('employee-directory-view-mode', viewMode);
    } catch (error) {
      console.error('Failed to save view mode to localStorage:', error);
    }
  }, [viewMode]);

  useEffect(() => {
    filterEmployees();
  }, [filterEmployees]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showSortOptions && !target.closest('.sort-dropdown')) {
        setShowSortOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortOptions]);

  const handleEmailClick = (email: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;
      window.open(gmailUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to open Gmail:', error);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDepartment('');
    setSelectedCountry('');
    setSelectedTimezone('');
    setShowAdvancedFilters(false);
    setSortBy(null);
  };

  const handleSortSelect = (sortOption: 'name-asc' | 'name-desc' | 'date-latest' | 'date-earliest') => {
    setSortBy(sortOption);
    setShowSortOptions(false);
  };

  const getSortLabel = () => {
    switch (sortBy) {
      case 'name-asc':
        return 'Name: A → Z';
      case 'name-desc':
        return 'Name: Z → A';
      case 'date-latest':
        return 'Start Date: Latest → Earliest';
      case 'date-earliest':
        return 'Start Date: Earliest → Latest';
      default:
        return 'Sort By';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-[#2563EB] dark:text-[#3B82F6]">Employee Database</h1>
        <div className="text-sm text-muted-foreground">
          {filteredEmployees.length} of {employees.length} employees
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <input
            type="text"
            placeholder="Search with name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-input rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6] focus:border-transparent"
          />
        </div>
      </div>

      {/* Advanced Filters, Sort By, and View Toggle on same line */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Advanced Filters
              <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
            </Button>
            
            {/* Sort By Button */}
            <div className="relative sort-dropdown">
              <Button
                variant="outline"
                onClick={() => setShowSortOptions(!showSortOptions)}
              >
                <ArrowUpDown className="h-4 w-4 mr-2" />
                {getSortLabel()}
                <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${showSortOptions ? 'rotate-180' : ''}`} />
              </Button>
              
              {/* Sort Options Dropdown */}
              {showSortOptions && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-card border border-input rounded-lg shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={() => handleSortSelect('name-asc')}
                      className={`w-full text-left px-4 py-2 hover:bg-accent hover:text-accent-foreground transition-colors ${
                        sortBy === 'name-asc' ? 'bg-accent text-accent-foreground' : ''
                      }`}
                    >
                      Name: A → Z
                    </button>
                    <button
                      onClick={() => handleSortSelect('name-desc')}
                      className={`w-full text-left px-4 py-2 hover:bg-accent hover:text-accent-foreground transition-colors ${
                        sortBy === 'name-desc' ? 'bg-accent text-accent-foreground' : ''
                      }`}
                    >
                      Name: Z → A
                    </button>
                    <button
                      onClick={() => handleSortSelect('date-latest')}
                      className={`w-full text-left px-4 py-2 hover:bg-accent hover:text-accent-foreground transition-colors ${
                        sortBy === 'date-latest' ? 'bg-accent text-accent-foreground' : ''
                      }`}
                    >
                      Start Date: Latest → Earliest
                    </button>
                    <button
                      onClick={() => handleSortSelect('date-earliest')}
                      className={`w-full text-left px-4 py-2 hover:bg-accent hover:text-accent-foreground transition-colors ${
                        sortBy === 'date-earliest' ? 'bg-accent text-accent-foreground' : ''
                      }`}
                    >
                      Start Date: Earliest → Latest
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* View Toggle */}
          <div className="flex rounded-lg bg-card">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('card')}
              className={`rounded-r-none ${
                viewMode === 'card'
                  ? 'bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#2563EB]/90 dark:hover:bg-[#3B82F6]/90'
                  : 'hover:bg-accent hover:text-accent-foreground'
              }`}
              style={{ border: 'none', boxShadow: 'none', outline: 'none' }}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('list')}
              className={`rounded-l-none ${
                viewMode === 'list'
                  ? 'bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#2563EB]/90 dark:hover:bg-[#3B82F6]/90'
                  : 'hover:bg-accent hover:text-accent-foreground'
              }`}
              style={{ border: 'none', boxShadow: 'none', outline: 'none' }}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {showAdvancedFilters && (
          <Card className="bg-gray-100 dark:bg-gray-800/50">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Department Filter */}
                <div>
                  <label className="block text-sm font-medium mb-2">Department</label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full p-2 border border-input rounded-md bg-background text-foreground"
                  >
                    <option value="">All Departments</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>

                {/* Country Filter */}
                <div>
                  <label className="block text-sm font-medium mb-2">Country</label>
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className="w-full p-2 border border-input rounded-md bg-background text-foreground"
                  >
                    <option value="">All Countries</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>

                {/* Timezone Filter */}
                <div>
                  <label className="block text-sm font-medium mb-2">Timezone</label>
                  <select
                    value={selectedTimezone}
                    onChange={(e) => setSelectedTimezone(e.target.value)}
                    className="w-full p-2 border border-input rounded-md bg-background text-foreground"
                  >
                    <option value="">All Timezones</option>
                    {timezones.map(timezone => (
                      <option key={timezone} value={timezone}>{timezone}</option>
                    ))}
                  </select>
                </div>
              </div>

              <Button variant="outline" onClick={clearFilters} size="sm">
                Clear All Filters
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Employee Display */}
      {employeesLoading ? (
        viewMode === 'card' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8].map(i => (
              <Card key={i} className="animate-pulse bg-gray-100 dark:bg-gray-800/50">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-muted rounded-full"></div>
                    <div className="w-32 h-4 bg-muted rounded"></div>
                    <div className="w-24 h-3 bg-muted rounded"></div>
                    <div className="w-28 h-3 bg-muted rounded"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {[1,2,3,4,5,6,7,8].map(i => (
              <Card key={i} className="animate-pulse bg-gray-100 dark:bg-gray-800/50">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-muted rounded-full"></div>
                    <div className="flex-1">
                      <div className="w-48 h-4 bg-muted rounded mb-2"></div>
                      <div className="w-32 h-3 bg-muted rounded"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredEmployees.map((employee) => (
            <Card
              key={employee.id}
              className="cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg bg-gray-100 dark:bg-gray-800/50"
              onClick={() => setSelectedEmployee(employee)}
            >
              <CardContent className="p-6">
                <div className="flex flex-col items-center space-y-4">
                  <Avatar className="w-20 h-20">
                    <AvatarImage 
                      src={employee.photoUrl || ''} 
                      alt={`${employee.firstName} ${employee.lastName}`}
                      className="object-contain"
                    />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xl">
                      {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-center">
                    <h3 className="font-semibold text-lg">
                      {employee.firstName} {employee.lastName}
                    </h3>
                    {(employee.personalEmail || employee.companyEmail) && (
                      <button
                        onClick={(e) => handleEmailClick(employee.personalEmail || employee.companyEmail || '', e)}
                        className="text-sm text-[#2563EB] dark:text-[#3B82F6] hover:underline block"
                      >
                        {employee.personalEmail || employee.companyEmail}
                      </button>
                    )}
                    <Badge variant="secondary" className="mt-2">
                      {employee.department}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredEmployees.map((employee) => (
            <Card
              key={employee.id}
              className="cursor-pointer transition-all duration-200 hover:shadow-md hover:bg-accent/50 bg-gray-100 dark:bg-gray-800/50"
              onClick={() => setSelectedEmployee(employee)}
            >
              <CardContent className="p-4">
                <div className="flex items-center space-x-4">
                  <Avatar className="w-12 h-12">
                    <AvatarImage 
                      src={employee.photoUrl || ''} 
                      alt={`${employee.firstName} ${employee.lastName}`}
                      className="object-contain"
                    />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-lg truncate">
                        {employee.firstName} {employee.lastName}
                      </h3>
                      <Badge variant="secondary" className="ml-2 flex-shrink-0">
                        {employee.department}
                      </Badge>
                    </div>
                    {(employee.personalEmail || employee.companyEmail) && (
                      <button
                        onClick={(e) => handleEmailClick(employee.personalEmail || employee.companyEmail || '', e)}
                        className="text-sm text-[#2563EB] dark:text-[#3B82F6] hover:underline block truncate"
                      >
                        {employee.personalEmail || employee.companyEmail}
                      </button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!employeesLoading && filteredEmployees.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No employees found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            margin: 0,
            padding: '2rem'
          }}
          onClick={() => setSelectedEmployee(null)}
        >
          <div
            className="bg-background rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-background border-b p-6 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold">
                {selectedEmployee.firstName} {selectedEmployee.lastName}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedEmployee(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-6 pt-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Section */}
                <div className="lg:col-span-1">
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardContent className="p-6 text-center">
                      <div className="mb-6 mt-4">
                        <Avatar className="w-40 h-40 mx-auto">
                          <AvatarImage 
                            src={selectedEmployee.photoUrl || ''} 
                            alt={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
                            className="object-contain"
                          />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-4xl">
                            {selectedEmployee.firstName?.charAt(0)}{selectedEmployee.lastName?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <h3 className="text-xl font-bold mb-2">
                        {selectedEmployee.firstName} {selectedEmployee.lastName}
                      </h3>
                      {selectedEmployee.preferredName && (
                        <p className="text-muted-foreground mb-2">
                          Preferred: {selectedEmployee.preferredName}
                        </p>
                      )}
                      <Badge variant="default" className="mb-4">
                        {selectedEmployee.department}
                      </Badge>
                      {selectedEmployee.namePronunciation && (
                        <p className="text-sm text-muted-foreground">
                          Pronunciation: {selectedEmployee.namePronunciation}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Details Section */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Contact Information */}
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Contact Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {selectedEmployee.companyEmail && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Company:</span>
                          <button
                            onClick={(e) => handleEmailClick(selectedEmployee.companyEmail || '', e)}
                            className="text-[#2563EB] dark:text-[#3B82F6] hover:underline text-left"
                          >
                            {selectedEmployee.companyEmail}
                          </button>
                        </div>
                      )}
                      {selectedEmployee.personalEmail && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Personal:</span>
                          <button
                            onClick={(e) => handleEmailClick(selectedEmployee.personalEmail || '', e)}
                            className="text-[#2563EB] dark:text-[#3B82F6] hover:underline text-left"
                          >
                            {selectedEmployee.personalEmail}
                          </button>
                        </div>
                      )}
                      {selectedEmployee.phoneNumber && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Phone:</span>
                          <span>{selectedEmployee.phoneNumber}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Work Information */}
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Briefcase className="h-5 w-5" />
                        Work Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {selectedEmployee.startDate && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Start Date:</span>
                          <span>{new Date(selectedEmployee.startDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      {selectedEmployee.timezone && (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Timezone:</span>
                          <span>{selectedEmployee.timezone}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Location Information */}
                  <Card className="bg-gray-100 dark:bg-gray-800/50">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5" />
                        Location
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {selectedEmployee.country && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Country:</span>
                          <span>{selectedEmployee.country}</span>
                        </div>
                      )}
                      {selectedEmployee.city && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">City:</span>
                          <span>{selectedEmployee.city}</span>
                        </div>
                      )}
                      {selectedEmployee.state && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">State:</span>
                          <span>{selectedEmployee.state}</span>
                        </div>
                      )}
                      {selectedEmployee.address1 && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Address:</span>
                          <span>{selectedEmployee.address1}</span>
                        </div>
                      )}
                      {selectedEmployee.zipCode && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Zip Code:</span>
                          <span>{selectedEmployee.zipCode}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Personal Information */}
                  {selectedEmployee.birthday && (
                    <Card className="bg-gray-100 dark:bg-gray-800/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="h-5 w-5" />
                          Personal Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Birthday:</span>
                          <span>{new Date(selectedEmployee.birthday).toLocaleDateString()}</span>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Emergency Contacts */}
                  {(selectedEmployee.emergencyContact1Name || selectedEmployee.emergencyContact2Name) && (
                    <Card className="bg-gray-100 dark:bg-gray-800/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="h-5 w-5" />
                          Emergency Contacts
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {selectedEmployee.emergencyContact1Name && (
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              <UserCheck className="h-4 w-4 text-muted-foreground" />
                              Primary Contact
                            </h4>
                            <p>{selectedEmployee.emergencyContact1Name}</p>
                            {selectedEmployee.emergencyContact1Method && (
                              <p className="text-sm text-muted-foreground">{selectedEmployee.emergencyContact1Method}</p>
                            )}
                            {selectedEmployee.emergencyContact1Relationship && (
                              <p className="text-sm text-muted-foreground">Relationship: {selectedEmployee.emergencyContact1Relationship}</p>
                            )}
                          </div>
                        )}
                        {selectedEmployee.emergencyContact2Name && (
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              <UserCheck className="h-4 w-4 text-muted-foreground" />
                              Secondary Contact
                            </h4>
                            <p>{selectedEmployee.emergencyContact2Name}</p>
                            {selectedEmployee.emergencyContact2Method && (
                              <p className="text-sm text-muted-foreground">{selectedEmployee.emergencyContact2Method}</p>
                            )}
                            {selectedEmployee.emergencyContact2Relationship && (
                              <p className="text-sm text-muted-foreground">Relationship: {selectedEmployee.emergencyContact2Relationship}</p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Additional Information */}
                  {(selectedEmployee.workVideo || selectedEmployee.contactAuthorities || selectedEmployee.lastUpdate) && (
                    <Card className="bg-gray-100 dark:bg-gray-800/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Briefcase className="h-5 w-5" />
                          Additional Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {selectedEmployee.workVideo && (
                          <div className="flex items-center gap-2">
                            <Video className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Work Video:</span>
                            <a
                              href={selectedEmployee.workVideo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#2563EB] dark:text-[#3B82F6] hover:underline"
                            >
                              View Video
                            </a>
                          </div>
                        )}
                        {selectedEmployee.contactAuthorities && (
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Contact Authorities:</span>
                            <span>{selectedEmployee.contactAuthorities}</span>
                          </div>
                        )}
                        {selectedEmployee.lastUpdate && (
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Last Updated:</span>
                            <span>{new Date(selectedEmployee.lastUpdate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Directory;